import { useEffect, useRef, type ReactNode, type RefObject } from 'react';
import { createPortal } from 'react-dom';
import { useFloatingPosition, type FloatingAlign } from '../hooks/useFloatingPosition';

interface FloatingMenuProps {
    open: boolean;
    triggerRef: RefObject<HTMLElement>;
    onClose: () => void;
    align?: FloatingAlign;
    /** Open above the trigger whenever there's more (or equal) room above than below. */
    preferUp?: boolean;
    /** Used only to decide which side has "enough" room - see useFloatingPosition. */
    estimatedHeight?: number;
    /** Force the menu's width to match the trigger's rendered width instead of sizing to its content. */
    matchTriggerWidth?: boolean;
    className?: string;
    children: ReactNode;
}

/**
 * Portals `children` to `document.body` as a `position: fixed` menu anchored
 * to `triggerRef`, so it's never clipped or scroll-unreachable inside a
 * scroll container. Also owns click-outside for both trigger and menu.
 */
export function FloatingMenu({
    open,
    triggerRef,
    onClose,
    align,
    preferUp,
    estimatedHeight,
    matchTriggerWidth,
    className,
    children
}: FloatingMenuProps) {
    const menuRef = useRef<HTMLDivElement>(null);
    const position = useFloatingPosition({
        open,
        triggerRef,
        align,
        preferUp,
        estimatedHeight,
        matchTriggerWidth
    });

    useEffect(() => {
        if (!open) return;
        const onPointerDown = (e: MouseEvent) => {
            const target = e.target as Node;
            if (triggerRef.current?.contains(target)) return;
            if (menuRef.current?.contains(target)) return;
            onClose();
        };
        document.addEventListener('mousedown', onPointerDown);
        return () => document.removeEventListener('mousedown', onPointerDown);
    }, [open, onClose, triggerRef]);

    if (!open || !position) return null;

    return createPortal(
        <div
            ref={menuRef}
            style={{
                position: 'fixed',
                zIndex: 9000, // below toasts (z-[9999])
                top: position.top,
                bottom: position.bottom,
                left: position.left,
                right: position.right,
                width: position.width,
                maxHeight: position.maxHeight,
                transform: position.centered ? 'translateX(-50%)' : undefined
            }}
            onMouseDown={(e) => e.stopPropagation()}
            className={className}
        >
            {children}
        </div>,
        document.body
    );
}
